import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedSegmentedControl } from "@/components/ThemedSegmentedControl";
import { useThemeColor } from "@/hooks/useThemeColor";
import {
  IDate,
  ISingleDate,
  DateMode,
  DatePrecision,
  parseDate,
  serializeDate,
  createSingleDate,
  formatSingleDate,
  formatDate,
  validateDate,
  compareSingleDates,
  getDaysInMonth,
  isValidTimeHHmm,
  MONTH_ABBREVIATIONS,
} from "common247";
import { DateSelector } from "@/components/date/DateSelector";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

export { DateSelector };

/**
 * Visual presentation variants for DateDisplay in display mode.
 */
export type DateDisplayVariant = "pill" | "badge" | "card" | "inline";

/**
 * Props for the DateDisplay component.
 */
export interface IDateDisplayProps {
  /**
   * The current date value.
   * Can be a JSON string, a legacy date string (e.g. "2026-10-09"), or an IDate object.
   */
  value?: string | IDate | null;

  /**
   * Callback fired whenever the user edits or updates the date.
   * Provides both the structured payload and the serialized JSON string.
   */
  onChange?: (payload: IDate, jsonString: string) => void;

  /**
   * Presentation mode: "display" for read-only rendering, or "editor" for active editing.
   */
  mode?: "display" | "editor";

  /**
   * Visual styling variant for display mode.
   * @default "badge"
   */
  variant?: DateDisplayVariant;

  /**
   * Whether editing is enabled. If false in editor mode, controls render read-only.
   * @default true
   */
  editable?: boolean;

  /**
   * Optional custom style applied to root container.
   */
  style?: ViewStyle;

  /**
   * Optional click handler in display mode (e.g. to open an editor or filter by date).
   */
  onPress?: () => void;

  /**
   * Whether to render the live JSON inspector preview below the editor.
   * @default true
   */
  showJsonInspector?: boolean;

  /**
   * Optional callback notifying parent of validation changes.
   */
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
}

const MONTH_OPTIONS = [
  { label: "Jan", value: 1 },
  { label: "Feb", value: 2 },
  { label: "Mar", value: 3 },
  { label: "Apr", value: 4 },
  { label: "May", value: 5 },
  { label: "Jun", value: 6 },
  { label: "Jul", value: 7 },
  { label: "Aug", value: 8 },
  { label: "Sep", value: 9 },
  { label: "Oct", value: 10 },
  { label: "Nov", value: 11 },
  { label: "Dec", value: 12 },
];

const PRECISION_LABELS: Record<DatePrecision, string> = {
  exact_datetime: "Exact Date/Time",
  exact_date: "Exact Date",
  approximate_datetime: "Approx. Date/Time (~)",
  approximate_date: "Approx. Date (~)",
  year_month: "Year & Month",
  year: "Year Only",
};

/**
 * Reusable DateDisplay component.
 * Supports exact date/time, exact date, approximate date/time, approximate date,
 * year/month, year only, and multi-date ranges.
 * Unpacks to and serializes from JSON cleanly.
 */
export function DateDisplay({
  value,
  onChange,
  mode = "display",
  variant = "badge",
  editable = true,
  style,
  onPress,
  showJsonInspector = false,
  onValidationChange,
}: IDateDisplayProps) {
  const tintColor = useThemeColor({}, "tint");
  const dangerColor = useThemeColor({}, "danger");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const readOnlyBg = useThemeColor({}, "inputReadOnly");

  // Normalized payload state
  const payload = useMemo(() => parseDate(value), [value]);

  const [dateMode, setDateMode] = useState<"single" | "range">(payload.type === "range" ? "range" : "single");
  const [singleDate, setSingleDate] = useState<ISingleDate>(
    payload.from || createSingleDate("exact_date", new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate())
  );
  const [fromDate, setFromDate] = useState<ISingleDate>(
    payload.from || createSingleDate("exact_date", new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate())
  );
  const [toDate, setToDate] = useState<ISingleDate>(
    payload.to || createSingleDate("exact_date", new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate() + 1)
  );

  // Field-level error messages
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  // Visual Picker modal visibility and anchor coordinates
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState<IPopoverAnchor | null>(null);

  useEffect(() => {
    const updated = parseDate(value);
    setDateMode(updated.type === "range" ? "range" : "single");
    if (updated.from) {
      setSingleDate(updated.from);
      setFromDate(updated.from);
    }
    if (updated.to) setToDate(updated.to);
  }, [value]);

  const currentPayload: IDate = useMemo(() => {
    return {
      type: dateMode === "range" ? "range" : "date",
      from: dateMode === "range" ? fromDate : singleDate,
      to: dateMode === "range" ? toDate : undefined,
      formatted: "",
    };
  }, [dateMode, singleDate, fromDate, toDate]);

  const validation = useMemo(() => {
    const res = validateDate(currentPayload);
    const activeFieldErrors = Object.values(fieldErrors).filter(Boolean) as string[];
    const combinedErrors = Array.from(new Set([...res.errors, ...activeFieldErrors]));
    return {
      isValid: res.isValid && activeFieldErrors.length === 0,
      errors: combinedErrors,
    };
  }, [currentPayload, fieldErrors]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errors);
    }
  }, [validation, onValidationChange]);

  const isRangeInverted = dateMode === "range" && compareSingleDates(fromDate, toDate) > 0;

  /**
   * Emits updated payload to parent callback.
   */
  const emitChange = (
    newMode: "single" | "range",
    newSingle: ISingleDate,
    newFrom: ISingleDate,
    newTo: ISingleDate
  ) => {
    if (!onChange) return;

    const newPayload: IDate = {
      type: newMode === "range" ? "range" : "date",
      from: newMode === "range" ? newFrom : newSingle,
      to: newMode === "range" ? newTo : undefined,
      formatted: "",
    };
    newPayload.formatted = formatDate(newPayload);
    const jsonStr = serializeDate(newPayload);
    onChange(newPayload, jsonStr);
  };

  /**
   * Updates a single date item with partial changes.
   */
  const updateSingleField = (
    target: "single" | "from" | "to",
    field: keyof ISingleDate,
    val: any
  ) => {
    const current = target === "single" ? singleDate : target === "from" ? fromDate : toDate;
    const isApprox =
      field === "precision"
        ? (val as DatePrecision).startsWith("approximate")
        : field === "isApproximate"
        ? Boolean(val)
        : current.isApproximate;

    const currentYear = field === "year" ? Number(val) : current.year;
    const currentMonth = field === "month" ? Number(val) : current.month;
    let clampedDay = field === "day" ? Number(val) : current.day;

    if (clampedDay !== undefined && currentMonth !== undefined) {
      const maxDays = getDaysInMonth(currentYear || 2026, currentMonth || 1);
      if (clampedDay > maxDays) clampedDay = maxDays;
      if (clampedDay < 1) clampedDay = 1;
    }

    const updated: ISingleDate = {
      ...current,
      [field]: val,
      day: clampedDay,
      isApproximate: isApprox,
    };
    updated.dateStr = formatSingleDate(updated);

    if (target === "single") {
      setSingleDate(updated);
      emitChange(dateMode, updated, fromDate, toDate);
    } else if (target === "from") {
      setFromDate(updated);
      emitChange(dateMode, singleDate, updated, toDate);
    } else {
      setToDate(updated);
      emitChange(dateMode, singleDate, fromDate, updated);
    }
  };

  // ==========================================
  // 1. DISPLAY MODE RENDERING
  // ==========================================
  if (mode === "display") {
    const isRange = payload.type === "range";
    const isApproximate =
      (isRange && (payload.from?.isApproximate || payload.to?.isApproximate)) ||
      (!isRange && payload.from?.isApproximate);

    const formattedText = payload.formatted || "No date set";

    const content = (
      <View style={[styles.displayBase, variant === "pill" && styles.displayPill, variant === "card" && [styles.displayCard, { backgroundColor: cardBg, borderColor }], variant === "badge" && [styles.displayBadge, { backgroundColor: badgeBg }], style]}>
        <Ionicons
          name={isRange ? "calendar-number-outline" : "calendar-outline"}
          size={variant === "pill" ? 12 : 14}
          color={tintColor}
          style={styles.displayIcon}
        />

        {isApproximate && !formattedText.includes("~") && (
          <View style={[styles.approxTag, { borderColor: tintColor }]}>
            <ThemedText style={[styles.approxTagText, { color: tintColor }]}>
              ~
            </ThemedText>
          </View>
        )}

        <ThemedText
          type={variant === "card" ? "defaultSemiBold" : "default"}
          style={[styles.displayText, variant === "pill" && styles.displayPillText]}
          numberOfLines={1}
        >
          {formattedText}
        </ThemedText>
      </View>
    );

    if (onPress) {
      return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
          {content}
        </TouchableOpacity>
      );
    }

    return content;
  }

  // ==========================================
  // 2. EDITOR MODE RENDERING
  // ==========================================
  const renderSingleDateEditor = (
    target: "single" | "from" | "to",
    data: ISingleDate,
    labelPrefix?: string
  ) => {
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const showDay = data.precision === "exact_datetime" || data.precision === "exact_date" || data.precision === "approximate_datetime" || data.precision === "approximate_date";
    const showMonth = data.precision !== "year";
    const showTime = data.precision === "exact_datetime" || data.precision === "approximate_datetime";

    const maxDays = getDaysInMonth(data.year || 2026, data.month || 1);
    const monthName =
      data.month && data.month >= 1 && data.month <= 12
        ? MONTH_ABBREVIATIONS[data.month - 1]
        : "Month";

    return (
      <View style={[styles.subEditorCard, { backgroundColor: readOnlyBg, borderColor }]}>
        {labelPrefix ? (
          <View style={styles.subEditorHeader}>
            <ThemedText type="defaultSemiBold" style={styles.subEditorTitle}>
              {labelPrefix}
            </ThemedText>
            <View style={styles.headerRightActions}>
              <ThemedText style={[styles.subEditorPreview, { color: tintColor }]}>
                {data.dateStr}
              </ThemedText>
              <TouchableOpacity
                style={[styles.smallPickerBtn, { borderColor: tintColor, backgroundColor: badgeBg }]}
                onPress={() => setPickerVisible(true)}
                activeOpacity={0.7}
                accessibilityLabel="Open visual calendar & clock picker"
              >
                <Ionicons name="calendar-outline" size={13} color={tintColor} />
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        {/* Precision selector */}
        <View style={styles.inputGroup}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Precision</ThemedText>
          <View style={styles.precisionRow}>
            {(
              [
                "exact_datetime",
                "exact_date",
                "approximate_datetime",
                "approximate_date",
                "year_month",
                "year",
              ] as DatePrecision[]
            ).map((prec) => {
              const active = data.precision === prec;
              return (
                <TouchableOpacity
                  key={prec}
                  style={[
                    styles.precisionPill,
                    {
                      borderColor: active ? tintColor : borderColor,
                      backgroundColor: active ? badgeBg : "transparent",
                    },
                  ]}
                  onPress={() => updateSingleField(target, "precision", prec)}
                  disabled={!editable}
                >
                  <ThemedText
                    style={[
                      styles.precisionPillText,
                      active ? { color: tintColor, fontWeight: "700" } : { color: textMuted },
                    ]}
                  >
                    {PRECISION_LABELS[prec]}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Dynamic input fields based on precision */}
        <View style={styles.fieldsRow}>
          {/* Year input */}
          <View style={[styles.fieldCol, { flex: 1.2 }]}>
            <ThemedText style={[styles.label, { color: textMuted }]}>Year</ThemedText>
            <ThemedInput
              value={data.year ? String(data.year) : ""}
              onChangeText={(txt) => {
                const clean = txt.replace(/[^0-9]/g, "");
                const num = parseInt(clean, 10);
                if (clean.length > 0 && (isNaN(num) || num < 1 || num > 9999)) {
                  setFieldErrors((prev) => ({ ...prev, [`${target}_year`]: "Year must be 1–9999" }));
                } else {
                  setFieldErrors((prev) => ({ ...prev, [`${target}_year`]: undefined }));
                }
                updateSingleField(target, "year", isNaN(num) ? undefined : num);
              }}
              placeholder="YYYY"
              keyboardType="number-pad"
              maxLength={4}
              editable={editable}
              containerStyle={styles.compactInputContainer}
              style={styles.compactInput}
            />
            {fieldErrors[`${target}_year`] && (
              <ThemedText style={[styles.fieldErrorText, { color: dangerColor }]}>
                {fieldErrors[`${target}_year`]}
              </ThemedText>
            )}
          </View>

          {/* Month input / Selector */}
          {showMonth && (
            <View style={[styles.fieldCol, { flex: 1.4 }]}>
              <ThemedText style={[styles.label, { color: textMuted }]}>Month</ThemedText>
              <View style={styles.monthPillsRow}>
                <ThemedInput
                  value={data.month ? String(data.month) : ""}
                  onChangeText={(txt) => {
                    const clean = txt.replace(/[^0-9]/g, "");
                    const num = parseInt(clean, 10);
                    if (clean.length > 0 && (isNaN(num) || num < 1 || num > 12)) {
                      setFieldErrors((prev) => ({ ...prev, [`${target}_month`]: "Month must be 1–12" }));
                    } else {
                      setFieldErrors((prev) => ({ ...prev, [`${target}_month`]: undefined }));
                    }
                    updateSingleField(target, "month", isNaN(num) ? undefined : num);
                  }}
                  placeholder="MM (1-12)"
                  keyboardType="number-pad"
                  maxLength={2}
                  editable={editable}
                  containerStyle={styles.compactInputContainer}
                  style={styles.compactInput}
                />
              </View>
              {fieldErrors[`${target}_month`] && (
                <ThemedText style={[styles.fieldErrorText, { color: dangerColor }]}>
                  {fieldErrors[`${target}_month`]}
                </ThemedText>
              )}
            </View>
          )}

          {/* Day input */}
          {showDay && (
            <View style={[styles.fieldCol, { flex: 1.2 }]}>
              <ThemedText style={[styles.label, { color: textMuted }]}>Day</ThemedText>
              <ThemedInput
                value={data.day ? String(data.day) : ""}
                onChangeText={(txt) => {
                  const clean = txt.replace(/[^0-9]/g, "");
                  const num = parseInt(clean, 10);
                  if (clean.length > 0 && (isNaN(num) || num < 1 || num > maxDays)) {
                    setFieldErrors((prev) => ({ ...prev, [`${target}_day`]: `Day 1–${maxDays}` }));
                  } else {
                    setFieldErrors((prev) => ({ ...prev, [`${target}_day`]: undefined }));
                  }
                  updateSingleField(target, "day", isNaN(num) ? undefined : num);
                }}
                placeholder="DD"
                keyboardType="number-pad"
                maxLength={2}
                editable={editable}
                containerStyle={styles.compactInputContainer}
                style={styles.compactInput}
              />
              {fieldErrors[`${target}_day`] && (
                <ThemedText style={[styles.fieldErrorText, { color: dangerColor }]}>
                  {fieldErrors[`${target}_day`]}
                </ThemedText>
              )}
            </View>
          )}

          {/* Time input */}
          {showTime && (
            <View style={[styles.fieldCol, { flex: 1.4 }]}>
              <ThemedText style={[styles.label, { color: textMuted }]}>Time (HH:mm)</ThemedText>
              <ThemedInput
                value={data.time || ""}
                onChangeText={(txt) => {
                  if (txt.length > 0 && !isValidTimeHHmm(txt)) {
                    setFieldErrors((prev) => ({ ...prev, [`${target}_time`]: "HH:mm (00:00–23:59)" }));
                  } else {
                    setFieldErrors((prev) => ({ ...prev, [`${target}_time`]: undefined }));
                  }
                  updateSingleField(target, "time", txt);
                }}
                placeholder="14:30"
                maxLength={5}
                editable={editable}
                containerStyle={styles.compactInputContainer}
                style={styles.compactInput}
              />
              {fieldErrors[`${target}_time`] && (
                <ThemedText style={[styles.fieldErrorText, { color: dangerColor }]}>
                  {fieldErrors[`${target}_time`]}
                </ThemedText>
              )}
            </View>
          )}
        </View>

        {/* Quick month pills */}
        {showMonth && (
          <View style={styles.quickMonthsGrid}>
            {MONTH_OPTIONS.map((m) => {
              const active = data.month === m.value;
              return (
                <TouchableOpacity
                  key={m.value}
                  style={[
                    styles.quickMonthBtn,
                    {
                      borderColor: active ? tintColor : borderColor,
                      backgroundColor: active ? tintColor : "transparent",
                    },
                  ]}
                  onPress={() => updateSingleField(target, "month", m.value)}
                  disabled={!editable}
                >
                  <ThemedText
                    style={[
                      styles.quickMonthText,
                      active ? { color: "#FFFFFF", fontWeight: "700" } : { color: textMuted },
                    ]}
                  >
                    {m.label}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.editorContainer, { backgroundColor: cardBg, borderColor }, style]}>
      {/* Mode toggle */}
      <View style={styles.modeToggleRow}>
        <View style={styles.headingAndPickerRow}>
          <ThemedText type="defaultSemiBold" style={styles.editorHeading}>
            Date Representation
          </ThemedText>

          <TouchableOpacity
            style={[styles.openPickerBtn, { borderColor: tintColor, backgroundColor: badgeBg }]}
            onPress={(e: any) => {
              const x = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX ?? 0;
              const y = e?.nativeEvent?.pageY ?? e?.nativeEvent?.clientY ?? 0;
              setPickerAnchor(x || y ? { x, y } : null);
              setPickerVisible(true);
            }}
            activeOpacity={0.7}
          >
            <Ionicons name="calendar-sharp" size={13} color={tintColor} />
            <ThemedText style={[styles.openPickerBtnText, { color: tintColor }]}>
              Visual Picker (Calendar & Clock)
            </ThemedText>
          </TouchableOpacity>
        </View>

        <ThemedSegmentedControl
          value={dateMode}
          onChange={(newMode) => {
            const m = newMode as "single" | "range";
            setDateMode(m);
            emitChange(m, singleDate, fromDate, toDate);
          }}
          options={[
            { label: "Single Date", value: "single" },
            { label: "Date Range (From → To)", value: "range" },
          ]}
        />
      </View>

      {/* Editor Body */}
      {dateMode === "single" ? (
        renderSingleDateEditor("single", singleDate)
      ) : (
        <View style={styles.rangeEditorsStack}>
          {renderSingleDateEditor("from", fromDate, "Start Date (From)")}
          <View style={styles.rangeDividerRow}>
            <View style={[styles.rangeLine, { backgroundColor: borderColor }]} />
            <View style={[styles.rangePill, { backgroundColor: isRangeInverted ? "rgba(220, 53, 69, 0.15)" : badgeBg }]}>
              <Ionicons
                name={isRangeInverted ? "alert-circle" : "arrow-down-outline"}
                size={14}
                color={isRangeInverted ? dangerColor : tintColor}
              />
              <ThemedText style={[styles.rangePillText, { color: isRangeInverted ? dangerColor : tintColor }]}>
                {isRangeInverted ? "Invalid Order" : "Span / Range"}
              </ThemedText>
            </View>
            <View style={[styles.rangeLine, { backgroundColor: borderColor }]} />
          </View>
          {isRangeInverted && (
            <View style={[styles.rangeValidationAlert, { backgroundColor: "rgba(220, 53, 69, 0.08)", borderColor: dangerColor }]}>
              <Ionicons name="alert-circle" size={15} color={dangerColor} />
              <ThemedText style={[styles.rangeValidationAlertText, { color: dangerColor }]}>
                Start date cannot be after end date ({fromDate.dateStr} is after {toDate.dateStr})
              </ThemedText>
            </View>
          )}
          {renderSingleDateEditor("to", toDate, "End Date (To)")}
        </View>
      )}

      {/* Presets bar */}
      <View style={styles.presetsBar}>
        <ThemedText style={[styles.presetsTitle, { color: textMuted }]}>
          Quick Presets:
        </ThemedText>
        <View style={styles.presetButtonsRow}>
          <ThemedButton
            title="Exact Now"
            variant="secondary"
            onPress={() => {
              const now = new Date();
              const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
              const s = createSingleDate("exact_datetime", now.getFullYear(), now.getMonth() + 1, now.getDate(), timeStr);
              setDateMode("single");
              setSingleDate(s);
              emitChange("single", s, fromDate, toDate);
            }}
          />
          <ThemedButton
            title="Approx. Today"
            variant="secondary"
            onPress={() => {
              const now = new Date();
              const s = createSingleDate("approximate_date", now.getFullYear(), now.getMonth() + 1, now.getDate());
              setDateMode("single");
              setSingleDate(s);
              emitChange("single", s, fromDate, toDate);
            }}
          />
          <ThemedButton
            title="Weekend Span"
            variant="secondary"
            onPress={() => {
              const now = new Date();
              const f = createSingleDate("exact_date", now.getFullYear(), now.getMonth() + 1, now.getDate());
              const t = createSingleDate("exact_date", now.getFullYear(), now.getMonth() + 1, now.getDate() + 1);
              setDateMode("range");
              setFromDate(f);
              setToDate(t);
              emitChange("range", singleDate, f, t);
            }}
          />
          <ThemedButton
            title="Year Only"
            variant="secondary"
            onPress={() => {
              const now = new Date();
              const s = createSingleDate("year", now.getFullYear());
              setDateMode("single");
              setSingleDate(s);
              emitChange("single", s, fromDate, toDate);
            }}
          />
        </View>
      </View>

      {/* Live Formatted Summary Preview & Validation State */}
      <View
        style={[
          styles.liveSummaryBox,
          {
            backgroundColor: validation.isValid ? badgeBg : "rgba(220, 53, 69, 0.08)",
            borderColor: validation.isValid ? borderColor : dangerColor,
            borderWidth: 1,
          },
        ]}
      >
        <Ionicons
          name={validation.isValid ? "sparkles-outline" : "alert-circle"}
          size={16}
          color={validation.isValid ? tintColor : dangerColor}
          style={{ marginRight: 6 }}
        />
        <ThemedText style={[styles.liveSummaryLabel, { color: validation.isValid ? textMuted : dangerColor }]}>
          {validation.isValid ? "Formatted Output:" : "Validation Errors:"}
        </ThemedText>
        <ThemedText
          type="defaultSemiBold"
          style={[styles.liveSummaryValue, { color: validation.isValid ? tintColor : dangerColor, flex: 1 }]}
        >
          {validation.isValid ? formatDate(currentPayload) : validation.errors.join("; ")}
        </ThemedText>
      </View>

      {/* JSON Inspector (Optional / Expandable) */}
      {showJsonInspector && (
        <View style={[styles.jsonInspectorContainer, { backgroundColor: readOnlyBg, borderColor }]}>
          <ThemedText style={[styles.jsonInspectorTitle, { color: textMuted }]}>
            📦 Stored JSON Payload (Unpackable Format):
          </ThemedText>
          <ThemedText style={styles.jsonText}>
            {serializeDate(currentPayload)}
          </ThemedText>
        </View>
      )}

      {/* Visual DateSelector Hover Popover */}
      <DateSelector
        visible={pickerVisible}
        onClose={() => setPickerVisible(false)}
        value={currentPayload}
        anchorPosition={pickerAnchor}
        onApply={(pickedPayload) => {
          setDateMode(pickedPayload.type === "range" ? "range" : "single");
          if (pickedPayload.from) {
            setFromDate(pickedPayload.from);
            setSingleDate(pickedPayload.from);
          }
          if (pickedPayload.to) setToDate(pickedPayload.to);
          setPickerVisible(false);
          if (onChange) {
            onChange(pickedPayload, serializeDate(pickedPayload));
          }
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  // Display styles
  displayBase: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
  },
  displayBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  displayPill: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.1)",
  },
  displayCard: {
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    width: "100%",
  },
  displayIcon: {
    marginRight: 2,
  },
  displayText: {
    fontSize: 13,
  },
  displayPillText: {
    fontSize: 11,
  },
  approxTag: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 3,
    paddingVertical: 1,
  },
  approxTagText: {
    fontSize: 9,
    fontWeight: "700",
    textTransform: "uppercase",
  },

  // Editor styles
  editorContainer: {
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 12,
  },
  editorHeading: {
    fontSize: 14,
  },
  headingAndPickerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  openPickerBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
  },
  openPickerBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  headerRightActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  smallPickerBtn: {
    padding: 4,
    borderRadius: 4,
    borderWidth: 1,
  },
  modeToggleRow: {
    gap: 8,
  },
  subEditorCard: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 10,
  },
  subEditorHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  subEditorTitle: {
    fontSize: 13,
  },
  subEditorPreview: {
    fontSize: 12,
    fontWeight: "700",
  },
  inputGroup: {
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  precisionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  precisionPill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
  },
  precisionPillText: {
    fontSize: 11,
  },
  fieldsRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  fieldCol: {
    gap: 2,
  },
  compactInputContainer: {
    marginVertical: 0,
  },
  compactInput: {
    height: 36,
    fontSize: 13,
    paddingVertical: 6,
  },
  monthPillsRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  quickMonthsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  quickMonthBtn: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 3,
    borderWidth: 1,
    minWidth: 32,
    alignItems: "center",
  },
  quickMonthText: {
    fontSize: 10,
  },
  rangeEditorsStack: {
    gap: 10,
  },
  rangeDividerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rangeLine: {
    flex: 1,
    height: 1,
  },
  rangePill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  rangePillText: {
    fontSize: 11,
    fontWeight: "600",
  },
  presetsBar: {
    gap: 6,
  },
  presetsTitle: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
  },
  presetButtonsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  liveSummaryBox: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  liveSummaryLabel: {
    fontSize: 12,
    marginRight: 6,
  },
  liveSummaryValue: {
    fontSize: 13,
  },
  jsonInspectorContainer: {
    padding: 10,
    borderRadius: 6,
    borderWidth: 1,
    gap: 4,
  },
  jsonInspectorTitle: {
    fontSize: 11,
    fontWeight: "700",
  },
  jsonText: {
    fontSize: 11,
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
    lineHeight: 16,
    opacity: 0.85,
  },
  fieldErrorText: {
    fontSize: 10,
    fontWeight: "600",
    marginTop: 2,
  },
  rangeValidationAlert: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    marginVertical: 4,
  },
  rangeValidationAlertText: {
    fontSize: 12,
    fontWeight: "600",
    flex: 1,
  },
});
